import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, MessageSquare, Clock, MapPin, ChevronRight,
  Loader2, Briefcase, Calendar, Settings, User
} from "lucide-react";
import { API } from "@/App";

export const ContractorMessagesPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);

  useEffect(() => {
    fetchJobsWithMessages();
  }, []);

  const fetchJobsWithMessages = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // Get all jobs that have messages
      const response = await axios.get(`${API}/jobs`, { headers });
      // Filter to only show jobs with messages or recent activity
      const jobsWithMessages = (response.data || []).filter(job => 
        job.messages?.length > 0 || job.status !== 'completed'
      );
      setJobs(jobsWithMessages);
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate("/contractor")}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Messages</h1>
              <p className="text-sm text-muted-foreground">Job conversations</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {jobs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-medium text-slate-700 mb-1">No messages yet</h3>
              <p className="text-sm text-muted-foreground">
                Messages from tenants and staff will appear here
              </p>
            </CardContent>
          </Card>
        ) : (
          jobs.map((job) => {
            const lastMessage = job.messages?.[job.messages.length - 1];
            return (
              <Card 
                key={job.job_id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/contractor/job/${job.job_id}`)}
                data-testid={`message-job-${job.job_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-slate-800">{job.title || "Maintenance Job"}</h3>
                        {job.messages?.length > 0 && (
                          <Badge variant="secondary" className="text-xs">
                            {job.messages.length} message{job.messages.length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                        <MapPin className="w-3 h-3" />
                        {job.property_address || "Address not available"}
                      </p>
                      {lastMessage && (
                        <div className="bg-slate-50 rounded-lg p-3 mt-2">
                          <p className="text-xs text-muted-foreground mb-1">
                            <User className="w-3 h-3 inline mr-1" />
                            {lastMessage.sender_name || "Unknown"} • {formatDate(lastMessage.timestamp)}
                          </p>
                          <p className="text-sm text-slate-700 line-clamp-2">
                            {lastMessage.content}
                          </p>
                        </div>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 mt-1" />
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Bottom Nav */}
      <nav className="bottom-nav">
        <div className="max-w-4xl mx-auto flex items-center justify-around">
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2"
            onClick={() => navigate("/contractor")}
          >
            <Briefcase className="w-5 h-5" />
            <span className="text-xs">Jobs</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2"
            onClick={() => navigate("/contractor/availability")}
          >
            <Calendar className="w-5 h-5" />
            <span className="text-xs">Diary</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2 text-primary"
          >
            <MessageSquare className="w-5 h-5" />
            <span className="text-xs">Messages</span>
          </Button>
          <Button 
            variant="ghost" 
            className="flex-col gap-1 h-auto py-2"
            onClick={() => navigate("/contractor/settings")}
          >
            <Settings className="w-5 h-5" />
            <span className="text-xs">Settings</span>
          </Button>
        </div>
      </nav>
    </div>
  );
};

export default ContractorMessagesPage;
